import { Box, CircularProgress, Grid, Typography } from '@material-ui/core'
import { createPickingList, deletePickingList, getUserPickingLists } from 'lib/api'
import { useDialog, useOrderStore } from 'lib/hooks'
import { useFetchOrders } from 'lib/hooks/useFetchOrders'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getPaidOrdersId, Order, PickingList } from 'shared'
import { DeletePickingListDialog } from '../DeletePickingListDialog'
import { PickingListBox } from './PickingListBox'

export interface PickingListPresenterProps {
  handleUpdateOrders: (updatedOrders: Order[]) => void
}

export const PickingListPresenter = ({
  handleUpdateOrders,
}: PickingListPresenterProps): JSX.Element => {
  const { t } = useTranslation()
  const { orders, userPickingLists, setUserPickingLists, loadingPickingLists } = useOrderStore()
  const { convertOrderResponseDtoToOrderClass } = useFetchOrders()

  const [openDeletePickingListDialog, setOpenDeletePickingListDialog] = useState<boolean>(false)
  const [selectedPickingList, setSelectedPickingList] = useState<PickingList | undefined>(undefined)
  const { enqueueSnackbar } = useSnackbar()
  const [setLoadingDialogOpen, LoadingDialog, loadingDialogOpen] = useDialog()

  const loadUserPickingLists = async () => {
    setLoadingDialogOpen(true)
    try {
      const newUserPickingLists = await getUserPickingLists('open')
      setUserPickingLists(newUserPickingLists)
    } catch (error) {
      console.error('getUserPickingLists failed:', error)
      enqueueSnackbar(t('orders.picking.errorLoadingPickingLists'), { variant: 'error' })
    } finally {
      setLoadingDialogOpen(false)
    }
  }

  const handleShowDeletePickingListDialog = (pickingList: PickingList) => {
    setSelectedPickingList(pickingList)
    setOpenDeletePickingListDialog(true)
  }

  const handleCloseDeletePickingListDialog = () => {
    setSelectedPickingList(undefined)
    setOpenDeletePickingListDialog(false)
  }

  const handleConfirmDeletePickingListDialog = () => {
    if (selectedPickingList) {
      handleDeletePickingList(selectedPickingList)
    }
    handleCloseDeletePickingListDialog()
  }

  const handleDeletePickingList = async (pickingList: PickingList) => {
    if (!pickingList) return

    // Deleting a picking list unlinks all its orders on the backend, which can take a while — keep the
    // loading dialog up for the whole delete + reload so the page isn't silently stuck without feedback.
    setLoadingDialogOpen(true)
    try {
      const rollbackedOrders = await deletePickingList(pickingList)
      const newOrders = rollbackedOrders.map((order) => convertOrderResponseDtoToOrderClass(order))
      handleUpdateOrders(newOrders)

      const newUserPickingLists = await getUserPickingLists('open')
      setUserPickingLists(newUserPickingLists)
    } catch (error) {
      console.error('deletePickingList failed:', error)
      enqueueSnackbar(t('orders.picking.errorDeletingPickingList'), { variant: 'error' })
    } finally {
      setLoadingDialogOpen(false)
    }
  }

  const handleNewAutomaticList = async () => {
    // automatic list from all paid orders
    const paidOrdersId = getPaidOrdersId(orders)

    if (paidOrdersId.length > 0) {
      try {
        const updatedOrders = await createPickingList(paidOrdersId)
        const newOrders = updatedOrders.map((order) => convertOrderResponseDtoToOrderClass(order))

        handleUpdateOrders(newOrders)
        await loadUserPickingLists()
      } catch (e: any) {
        console.error('createPickingList failed:', e)
        const detail = e?.response?.data?.error
        const fallback = t('orders.picking.errorCreatingPickingList')
        // ordersNotLinkable: some orders on screen are already in a list or no longer to pick
        const message =
          detail === 'ordersNotLinkable'
            ? t('orders.picking.ordersNotLinkable')
            : detail
            ? `${fallback}: ${detail}`
            : fallback
        enqueueSnackbar(message, { variant: 'error' })
      }
    } else {
      enqueueSnackbar(t('orders.picking.noOrdersToPick'), { variant: 'error' })
    }
  }

  return (
    <>
      {loadingDialogOpen ? (
        <>
          <LoadingDialog
            title="Loading..."
            content={
              <Box marginTop={3} height="8rem" width="fit-content" margin="0 auto">
                <CircularProgress size={100} />
              </Box>
            }
          />
        </>
      ) : loadingPickingLists ? (
        <Box
          textAlign="center"
          paddingTop={5}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Typography variant="button" color="primary">
            {t('common.loading')}
          </Typography>
          <Box marginTop={3}>
            <CircularProgress size={100} />
          </Box>
        </Box>
      ) : (
        <>
          <Box>
            {userPickingLists.length > 0 && (
              <Box textAlign="center" paddingTop={5}>
                <Typography variant="button" color="primary">
                  {t('orders.picking.startPicking')}
                </Typography>

                <Grid container justifyContent="center">
                  {userPickingLists.map((pl) => (
                    <Grid item key={pl.name + pl.cardmarketUsername + pl.state}>
                      <Box padding={1}>
                        <PickingListBox
                          pickingList={pl}
                          handleShowDeletePickingListDialog={() =>
                            handleShowDeletePickingListDialog(pl)
                          }
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Box textAlign="center" paddingTop={5}>
              <Typography variant="button" color="primary">
                {t('orders.picking.createLists')}
              </Typography>

              <Grid container justifyContent="center">
                <Box padding={1}>
                  <PickingListBox
                    title={t('orders.picking.automaticList')}
                    subtitle={t('orders.picking.fromPaidOrders')}
                    handleNewAutomaticList={handleNewAutomaticList}
                    automaticList
                  />
                </Box>
                <Box padding={1}>
                  <PickingListBox
                    title={t('orders.picking.manualList')}
                    subtitle={t('orders.picking.fromSelectedOrders')}
                  />
                </Box>
              </Grid>
            </Box>
          </Box>

          <DeletePickingListDialog
            open={openDeletePickingListDialog}
            pickingList={selectedPickingList}
            onClose={handleCloseDeletePickingListDialog}
            onConfirm={handleConfirmDeletePickingListDialog}
          />
        </>
      )}
    </>
  )
}
